import { isValidUuid, Locales, QuestionType } from "@simplyk/common";
import { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import { v4 } from "uuid";

import { RateOption, TicketingChoiceV3, TicketingFormQuestionRowV3 } from "../types";

import { AppRouterType } from "@/common/helpers/trpc";
import { QuestionOutput } from "@/common/types/trpc";

type QuestionStepDataItem = inferRouterOutputs<AppRouterType>["getTicketingQuestionsStepData"]["questions"][number];
type QuestionInputV3 = inferRouterInputs<AppRouterType>["updateTicketingQuestionsV3"]["questions"][number];

export const mapApiDataToRows = (
  apiQuestions: QuestionStepDataItem[],
  rateOptions: RateOption[]
): TicketingFormQuestionRowV3[] =>
  apiQuestions.map((item) => {
    const isMaximumActive = item.choices.some((c) => c.maximum !== null);

    // Drop rate IDs pointing to deleted rates so stale UUIDs never surface as chips.
    const ratesAssigned: RateOption[] =
      item.ratesAssigned?.flatMap((rateId) => {
        const rate = rateOptions.find((r) => r.value === rateId);
        return rate ? [rate] : [];
      }) ?? [];

    const askForAllRates = item.scope === "participant" ? ratesAssigned.length === rateOptions.length : true;

    return {
      localId: v4(),
      questionId: item.questionId,
      crmCustomPropertyId: item.crmCustomPropertyId,
      body: item.body,
      type: item.type,
      required: item.required,
      scope: item.scope,
      choices: item.choices.map((c) => ({
        id: c.id,
        body: c.body,
        sortIndex: c.sortIndex,
        maximum: c.maximum,
      })),
      sortIndex: item.sortIndex,
      isActiveOnForm: item.isLinkedToForm,
      isNew: false,
      isDirty: false,
      askForAllRates,
      ratesAssigned,
      isMaximumActive,
      countAlreadyAnswered: item.countAlreadyAnswered,
    };
  });

const buildDefaultMemberQuestionRow = (
  type: QuestionType,
  body: string,
  required: boolean
): TicketingFormQuestionRowV3 => ({
  localId: v4(),
  questionId: null,
  crmCustomPropertyId: null,
  body,
  type,
  required,
  scope: "participant",
  choices: [],
  sortIndex: 0,
  isActiveOnForm: true,
  isNew: true,
  isDirty: true,
  askForAllRates: true,
  ratesAssigned: [],
  isMaximumActive: false,
  countAlreadyAnswered: 0,
});

export const withDefaultMemberQuestions = (
  rows: TicketingFormQuestionRowV3[],
  bodies: { name: string; email: string }
): TicketingFormQuestionRowV3[] => {
  const hasActiveMemberName = rows.some(
    (row) => row.scope === "participant" && row.type === QuestionType.Name && row.isActiveOnForm
  );
  const hasPersistedMemberQuestion = rows.some((row) => row.scope === "participant" && row.questionId !== null);
  const hasMemberEmail = rows.some((row) => row.scope === "participant" && row.type === QuestionType.Email);

  const defaults: TicketingFormQuestionRowV3[] = [];
  if (!hasActiveMemberName) {
    defaults.push(buildDefaultMemberQuestionRow(QuestionType.Name, bodies.name, true));
  }
  if (!hasMemberEmail && !hasPersistedMemberQuestion) {
    defaults.push(buildDefaultMemberQuestionRow(QuestionType.Email, bodies.email, false));
  }
  return [...defaults, ...rows];
};

// A per-attendee question with the toggle off and nothing picked is asked to nobody: invisible per guest
// and invisible to the buyer. Only forms broken before ticket-type deletion started archiving these can
// still hold one, so saving the step retires it rather than refusing the save over damage the organizer
// did not cause. Leaving it in would send `ratesAssigned: []`, which the API rejects outright.
const isAskedToNobody = (row: TicketingFormQuestionRowV3): boolean =>
  row.scope === "participant" && !row.askForAllRates && row.ratesAssigned.length === 0;

export const toV3Input = (rows: TicketingFormQuestionRowV3[]): QuestionInputV3[] =>
  rows
    .filter((row) => row.isActiveOnForm && !isAskedToNobody(row))
    .map((row) => {
      const computeRatesAssigned = (): string[] | null => {
        // `null` is the API's "every ticket type", and the server resolves which rates those are. Sending
        // the id list instead would send `[]` on a form with none — the shape that means "asked to nobody".
        if (row.scope !== "participant" || row.askForAllRates) {
          return null;
        }
        return row.ratesAssigned.map((r) => r.value);
      };

      return {
        id: row.questionId ?? undefined,
        crmCustomPropertyId: row.crmCustomPropertyId ?? undefined,
        body: row.body,
        type: row.type,
        required: row.required,
        scope: row.scope,
        ratesAssigned: computeRatesAssigned(),
        choices: row.choices.map((c) => ({
          id: row.questionId && c.id && isValidUuid(c.id) ? c.id : undefined,
          body: c.body,
          maximum: row.isMaximumActive ? c.maximum : null,
        })),
      };
    });

export const mapChoicesFromFormData = (
  choices: Array<{ id?: string; body: string; maximum?: number | null }>
): TicketingChoiceV3[] =>
  choices.map((c, idx) => ({
    ...(c.id && { id: c.id }),
    body: c.body,
    sortIndex: idx,
    maximum: c.maximum ?? null,
  }));

export const mapV3QuestionsToPreviewFormat = (
  questions: TicketingFormQuestionRowV3[],
  locale: Locales,
  rateQuestions: { id: string; rateId: string }[]
): QuestionOutput[] =>
  questions
    .filter((q) => q.isActiveOnForm)
    .map((question, index) => ({
      id: question.questionId ?? v4(),
      type: question.type,
      questionFields: [{ id: v4(), locale, body: question.body }],
      countAlreadyAnswered: question.countAlreadyAnswered,
      required: question.required,
      choices: question.choices.map((c) => ({
        id: c.id ?? v4(),
        label: c.body,
        choiceFields: [{ id: v4(), locale, body: c.body }],
        maximum: c.maximum,
        sortIndex: c.sortIndex,
      })),
      hasRate: question.scope === "participant",
      rateQuestions:
        question.scope === "participant"
          ? rateQuestions.filter(
              (rq) => question.askForAllRates || question.ratesAssigned.some((r) => r.value === rq.rateId)
            )
          : [],
      sortIndex: index,
    }));

export const buildPlaceholderQuestions = (rateQuestions: { id: string; rateId: string }[]): QuestionOutput[] => [
  {
    id: v4(),
    type: QuestionType.Name,
    countAlreadyAnswered: 0,
    required: false,
    hasRate: true,
    rateQuestions,
    sortIndex: 0,
  },
];
